<template>
  <v-dialog
    :value="value" @input="$emit('input')"
    width="990"
  >

    <v-card>
      <v-card-title class="text-h5">
        <template v-if="item.paysystem_id">Update Paymethod #{{ item.paymethod_id }}</template>
        <template v-else>Create Paymethod</template>
      </v-card-title>

      <v-card-text>

        <v-row>
          <v-col cols="5">
            <select-project
              class="mb-2"
              :loading="loading"
              :disabled="loading"
              v-model="item.entity_id"
              required
              :return-object="false"
            >

            </select-project>

            <v-autocomplete
              class="mb-2"
              label="Paysystem"
              :loading="loading"
              :disabled="loading"
              v-model="item.paysystem_id"
              :prefix="'#' + (item.paysystem_id || '')"
              :items="paysystems"
              :item-text="item => `${item.name} (${item.paysystem_id})`"
              item-value="paysystem_id"
              auto-select-first
              hide-details
              required
              :return-object="false"
            >
              <template v-slot:selection="{ item }">
                {{ item.name }} {{ item.comments ? `(${item.comments})` : '' }}
              </template>
              <template v-slot:item="{ item }">
                {{ item.name }} ({{ item.paysystem_id }}) {{ item.comments ? `(${item.comments})` : '' }}
              </template>
            </v-autocomplete>

            <v-text-field
              class="mb-2"
              label="Title"
              :loading="loading"
              :disabled="loading"
              v-model="item.title"
              hide-details
              required
            />

            <v-text-field
              class="mb-2"
              label="Alias"
              :loading="loading"
              :disabled="loading"
              v-model="item.name"
              hide-details
              required
            />

            <v-text-field
              class="mb-2"
              label="Internal Alias"
              :loading="loading"
              :disabled="loading"
              v-model="item.internal_name"
              hide-details
              required
            />

            <v-text-field
              class="mb-2"
              label="Code"
              :loading="loading"
              :disabled="loading"
              v-model="item.code"
              hide-details
              required
            />

            <v-combobox
              class="mb-2"
              label="Type"
              :loading="loading"
              :disabled="loading"
              :items="types"
              v-model="item.type"
              hide-details
            />

            <v-autocomplete
              class="mb-2"
              label="Settings Template"
              :loading="loading || loadingSettings"
              :disabled="loading || loadingSettings"
              v-model="template"
              :items="templates"
              clearable
              auto-select-first
              item-text="alias"
              item-value="paysystem"
              append-outer-icon="mdi-arrow-right-bold-outline"
              @click:append-outer="applyTemplate"
              hide-details
              :return-object="true"
            />
          </v-col>

          <v-col cols="7">
            <v-label>Settings</v-label>

            <vue-json-editor
              :loading="loading"
              :disabled="loading"
              v-model="item.settings"
              mode="code"
              :modes="[]"
              :show-btns="false"
              :exapndedOnStart="true"
            />
            <v-btn-toggle
              :loading="loading"
              :disabled="loading"
              v-model="json_predefine"
              multiple
              class="settings-shortcut-btn-group"
            >
              <v-btn
                x-small
                value="whitelistWithDeposits"
                @click="toggleDeep(settingDefaults.whitelistWithDeposits.key , !json_predefine.includes(settingDefaults.whitelistWithDeposits.key))"
              >
                Whitelist
              </v-btn>
              <v-btn
                x-small
                value="whitelistWithAffiliate"
                @click="toggleDeep(settingDefaults.whitelistWithAffiliate.key , !json_predefine.includes(settingDefaults.whitelistWithAffiliate.key))"
              >
                Whitelist (Affiliate)
              </v-btn>
              <v-btn
                x-small
                value="iban_field"
                @click="settingsToggle('iban_field' , !json_predefine.includes('iban_field'))"
              >
                IBAN Field
              </v-btn>
              <v-btn
                x-small
                value="convert_to"
                @click="settingsToggle('convert_to_array' , !json_predefine.includes('convert_to'))"
              >
                Convert (List)
              </v-btn>
              <v-btn
                x-small
                value="convert_to"
                @click="settingsToggle('convert_to_set' , !json_predefine.includes('convert_to'))"
              >
                Convert (Set)
              </v-btn>
              <v-btn
                x-small
                value="convert_to"
                @click="settingsToggle('convert_to' , !json_predefine.includes('convert_to'))"
              >
                Convert
              </v-btn>
              <v-btn
                x-small
                value="fast_withdraw"
                @click="settingsToggle('fast_withdraw', !json_predefine.includes('fast_withdraw'))"
              >
                Fast Withdraw
              </v-btn>
              <template
                v-if="json_predefine.includes('fast_withdraw')"
              >
                <v-btn
                  v-for="(_, key) in FAST_WITHDRAW_SETTINGS"
                  :key="key"
                  x-small
                  :value="FAST_WITHDRAW_PREFIX + key"
                  @click="toggleDeep(FAST_WITHDRAW_PREFIX + key, !json_predefine.includes(FAST_WITHDRAW_PREFIX + key), 'fast_withdraw')"
                >
                  {{ key }}
                </v-btn>
              </template>
            </v-btn-toggle>

            <non-ascii-alert :source-object="item.settings" />
          </v-col>
        </v-row>

        <fieldset v-if="template" class="px-2 pb-2 group-border">
          <legend class="px-2">Template Legend</legend>

          <v-list-item
            v-for="item of template.paymethod"
            dense
            class="settings-legend"
            :key="item.key"
          >
            <v-list-item-icon>
              <v-icon>mdi-circle-small</v-icon>
            </v-list-item-icon>
            <v-list-item-content class="py-1">
              <v-list-item-title>
                {{ item.key }} ({{ item.type }})
                <template v-if="item.default">, Default: {{ item.default }}</template>
              </v-list-item-title>
              <v-list-item-subtitle v-if="item.comment">
                {{ item.comment }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </fieldset>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn
          color="warning"
          text
          :loading="loading"
          :disabled="loading"
          @click.native="$emit('input')"
        >
          Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="info"
          text
          :loading="loading"
          :disabled="loading"
          @click="saveAs(item)"
        >
          Save as
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          :loading="loading"
          :disabled="loading"
          @click="save(item)"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .jsoneditor-menu {
    display: none !important;
  }

  div.jsoneditor-mode-code {
    height: 340px;
  }

  div.jsoneditor-outer {
    padding-top: 0px;
    margin-top: 0px;
  }

  .float-right {
    overflow-x: scroll;
  }

  .settings-shortcut-btn-group {
    display: flex;
    flex-wrap: wrap;

    .v-btn {
      flex-grow: 1;
      border-left-width: 1px !important;
      border-radius: 0 !important;
    }
  }
</style>

<script>
  import vueJsonEditor from 'vue-json-editor'

  import { API } from '@/utils/api'
  import { isEmpty, omit, find, includes } from 'lodash'
  import SelectProject from '@/components/core/SelectProject/SelectProject.vue'
  import NonAsciiAlert from '../NonAsciiAlert.vue'

  const WHITELIST_DEPOSITS = {
    deposit_sum: 0,
    deposit_count: 0
  }

  const WHITELIST_AFFILIATE = {
    affiliate_tag: true,
  }

  const FAST_WITHDRAW_SETTINGS = {
    only_verified: true,
    only_primary_verified: true,
    vip: true,
    level: [],
    countries: [],
    only_approved: true,
  }

  const FAST_WITHDRAW_PREFIX = 'fast_withdraw:'

  const DEFAULT_SETTINGS = {
    whitelistWithDeposits: {
      value: {
        ...WHITELIST_DEPOSITS
      },
      key: 'whitelistWithDeposits',
    },
    whitelistWithAffiliate: {
      value: {
        ...WHITELIST_AFFILIATE
      },
      key: 'whitelistWithAffiliate'
    },
    convert_to: {
      value: '',
      key: 'convert_to'
    },
    convert_to_array: {
      value: [ '' ],
      key: 'convert_to'
    },
    convert_to_set: {
      value: { '': '' },
      key: 'convert_to'
    },
    iban_field: {
      value: 'account',
      key: 'ibanField'
    },
    fast_withdraw: {
      value: {
        active: true,
      },
      key: 'fast_withdraw',
    },
    ...Object.fromEntries(Object.keys(FAST_WITHDRAW_SETTINGS).map(key => [
      FAST_WITHDRAW_PREFIX + key, ({ key, value: { [key]: FAST_WITHDRAW_SETTINGS[key] }, })],
    )),
  }

  export default {
    props: {
      paysystems: { default: () => ([]) },
      types: { default: () => ([]) },
      value: { default: false },
      refresh: { default: false },
      item: { default: () => ({}) },
    },

    components: {
      SelectProject,
      vueJsonEditor,
      NonAsciiAlert,
    },

    data: () => ({
      loadingSettings: false,
      loading: false,
      template: null,
      templates: [],
      settingDefaults: DEFAULT_SETTINGS,
      json_predefine: [],
      ignore: {
        json_predefine: true
      },
      headers: [
        { text: 'Key', value: 'key', sortable: false },
        { text: 'Value', value: 'value', sortable: false },
      ],
      FAST_WITHDRAW_SETTINGS,
      FAST_WITHDRAW_PREFIX,
    }),

    watch: {
      'item.paysystem_id' () {
        let paysystemName = (this.paysystemByID(this.item.paysystem_id) || {}).name

        this.template = find(this.templates, { alias: paysystemName })

        if ((this.item.name || '').length == 0) {
          this.item.name = paysystemName
        }
      },
      template () {
        if (isEmpty(this.item.settings)) this.applyTemplate()
      },
      value () {
        if (this.value) {

          this.loadPaymentSettings()
          this.template = null

          if (this.item.settings?.whitelist) {
            if (Object.keys(WHITELIST_DEPOSITS).some(item => item in this.item.settings?.whitelist)) {
              this.json_predefine.push(this.settingDefaults.whitelistWithDeposits.key)
            }
            if ('affiliate_tag' in this.item.settings.whitelist) {
              this.json_predefine.push(this.settingDefaults.whitelistWithAffiliate.key)
            }
          }
          if (this.item.settings.convert_to) {
            this.json_predefine.push('convert_to')
          }
          if (this.item.settings.ibanField) {
            this.json_predefine.push('iban_field')
          }
          if (this.item.settings.fast_withdraw) {
            this.json_predefine.push('fast_withdraw')
            for (const key in FAST_WITHDRAW_SETTINGS) {
              if (key in this.item.settings.fast_withdraw) {
                this.json_predefine.push(FAST_WITHDRAW_PREFIX + key)
              }
            }
          }

          this.$nextTick(function () {
            this.ignore.json_predefine = false
          })
        } else {
          this.$nextTick(function () {
            this.ignore.json_predefine = true
            this.json_predefine = []
          })
        }
      }
    },

    methods: {
      applyTemplate () {
        this.item.settings = {}

        if (!this.template) return

        let defaultValues = {
          string: '',
          number: 0,
          bool: false,
          array: [],
          default: null
        }

        let system = [ 'code', 'name', 'title' ]

        for (let item of this.template.paymethod) {

          let value = defaultValues.default
          if (item.hasOwnProperty('default')) {
            value = item.default
          } else if (defaultValues.hasOwnProperty(item.type)) {
            value = defaultValues[item.type]
          }

          if (!includes(system, item.key)) {
            this.item.settings[item.key] = value
          }
        }
      },

      settingsToggle (name, toggle) {
        this.item.settings = { ...this.item.settings }
        if (toggle && this.settingDefaults[name]) {

          this.item.settings[this.settingDefaults[name].key] = this.settingDefaults[name].value

        } else if (!toggle && this.settingDefaults[name]) {
          this.item.settings[this.settingDefaults[name].key] = undefined
        }
      },

      toggleDeep(key, isAdding, parent = 'whitelist') {
        this.item.settings = { ...this.item.settings }
        const target = { ...(this.item.settings?.[parent] || {}) }
        const defaults = this.settingDefaults[key]
        const values = defaults.value

        if (isAdding) {
          Object.assign(target, values)
        } else {
          for (const key of Object.keys(values)) {
            delete target[key]
          }
        }

        this.$set(this.item.settings, parent, Object.keys(target).length ? target : undefined)
      },

      paysystemByID (id) {
        return find(this.paysystems, { paysystem_id: id })
      },
      keys (obj) {
        if (!obj) return {}
        return Object.keys(obj)
      },
      loadPaymentSettings () {
        this.loadingSettings = true
        return API.call('/settings/payment/settings.json', {}).then(resp => {
          this.templates = resp.data
          if (this.item.paysystem_id) {
            this.template = find(this.templates, { alias: (this.paysystemByID(this.item.paysystem_id) || {}).name })
          }

        }).finally(() => {
          this.loadingSettings = false
        })
      },
      async save (item, message = 'Save paymethod', saveAndClose = true) {
        let buttons = []
        if (!saveAndClose) {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        } else {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Save and close', value: 'close', class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        }

        let confirmRes = await this.$root.$confirm('Confirm', message, { width: 515, buttons })

        if (!confirmRes) {
          return
        }

        this.loading = true
        return API.call('/$v/paymethod/update.json', { items: [ item ] }, null, { entity_id: item.entity_id }).then(resp => {

          this.$notification.show = true
          this.$notification.message = 'Paymethods updated'
          this.$notification.type = 'success'

          return this.refresh()
        }).finally(() => {
          this.loading = false

          if (confirmRes == 'close') {
            this.$emit('input')
          }
        })
      },
      async saveAs (item) {
        return this.save(omit(item, 'paymethod_id'), 'Save paymethod as new', false)
      }
    }
  }
</script>
